// ============================================
// Safety Checker Orchestration Engine
//
// Deterministic safety gate that runs BEFORE any
// protocol is displayed to the patient. Aggregates
// safety data from all sources (modals + chat
// disclosures), validates protocols, triggers
// regeneration on exclusions, and audit-logs
// every applied exclusion with rule ID + reasoning.
// ============================================

import type { Protocol } from '../types';
import type {
  SafetyRule,
  SafetyViolation,
  Product,
} from '../types/formulary';
import { runSafetyChecks, preFilterProducts } from './safety/safetyChecker';
import { getActiveSafetyRules, getAllProducts } from './firestore/formularyService';
import { logAudit } from '../utils/auditLog';

// ── Types ──

/** All safety-relevant patient data, aggregated from every source. */
export interface SafetyDataProfile {
  /** Patient sex from triage modal */
  sex?: 'female' | 'male';
  /** Patient age from triage modal */
  age?: number;
  /** Pregnancy/reproductive status from pregnancy modal */
  isPregnant?: boolean;

  /** Medications from meds modal */
  modalMedications: string[];
  /** Allergies from allergies modal */
  modalAllergies: string[];
  /** Conditions from intake */
  modalConditions: string[];

  /** Medications disclosed during chat (HLX-B09 disclosures) */
  chatMedications: string[];
  /** Allergies disclosed during chat */
  chatAllergies: string[];
  /** Conditions disclosed during chat */
  chatConditions: string[];
}

/** Record of a single exclusion applied to a protocol. */
export interface SafetyExclusionRecord {
  /** The safety rule that triggered this exclusion */
  ruleId: string;
  ruleName: string;
  /** block | warn | info */
  severity: string;
  /** Which products were excluded */
  affectedProducts: string[];
  /** The rule's message explaining why */
  reasoning: string;
  /** Where the triggering data came from */
  dataSource: 'modal' | 'chat' | 'both' | 'deterministic';
  /** ISO timestamp */
  timestamp: string;
}

/** Result of the full safety orchestration cycle. */
export interface SafetyOrchestratorResult {
  /** The validated (and possibly regenerated) protocol */
  protocol: Protocol;
  /** Whether the original protocol passed without exclusions */
  passedInitialCheck: boolean;
  /** All exclusions that were applied */
  exclusions: SafetyExclusionRecord[];
  /** All warnings (non-blocking) */
  warnings: SafetyViolation[];
  /** Informational notes */
  info: SafetyViolation[];
  /** The aggregated safety profile used for validation */
  aggregatedProfile: AggregatedSafetyData;
}

/** Merged safety data after deduplication. */
export interface AggregatedSafetyData {
  medications: string[];
  allergies: string[];
  conditions: string[];
  sex?: 'female' | 'male';
  age?: number;
  isPregnant?: boolean;
  sources: {
    modalMedCount: number;
    chatMedCount: number;
    modalAllergyCount: number;
    chatAllergyCount: number;
    modalConditionCount: number;
    chatConditionCount: number;
  };
}

// ── Aggregation ──

/**
 * Merge safety data from all sources (modals + chat disclosures).
 * Deduplicates by lowercase normalization.
 * [c4] Re-validates against the latest safety data from all sources.
 */
export function aggregateSafetyProfile(profile: SafetyDataProfile): AggregatedSafetyData {
  const dedup = (a: string[], b: string[]): string[] => {
    const seen = new Set<string>();
    const result: string[] = [];
    for (const item of [...a, ...b]) {
      const key = item.toLowerCase().trim();
      if (key && !seen.has(key)) {
        seen.add(key);
        result.push(key);
      }
    }
    return result;
  };

  return {
    medications: dedup(profile.modalMedications, profile.chatMedications),
    allergies: dedup(profile.modalAllergies, profile.chatAllergies),
    conditions: dedup(profile.modalConditions, profile.chatConditions),
    sex: profile.sex,
    age: profile.age,
    isPregnant: profile.isPregnant,
    sources: {
      modalMedCount: profile.modalMedications.length,
      chatMedCount: profile.chatMedications.length,
      modalAllergyCount: profile.modalAllergies.length,
      chatAllergyCount: profile.chatAllergies.length,
      modalConditionCount: profile.modalConditions.length,
      chatConditionCount: profile.chatConditions.length,
    },
  };
}

// ── Audit Logging ──

/**
 * [c3] Log every applied exclusion to the audit trail.
 * Each exclusion records the SafetyRule.id and the reasoning.
 */
function logSafetyExclusions(exclusions: SafetyExclusionRecord[], protocolId: string): void {
  for (const exclusion of exclusions) {
    logAudit('safety.exclusion_applied', `protocol:${protocolId}`, {
      ruleId: exclusion.ruleId,
      ruleName: exclusion.ruleName,
      severity: exclusion.severity,
      affectedProducts: exclusion.affectedProducts.join(', '),
      reasoning: exclusion.reasoning,
      dataSource: exclusion.dataSource,
    });
  }
}

/**
 * Determine which data source triggered a rule, based on what
 * matched in modal vs chat data.
 */
function classifyDataSource(
  rule: SafetyViolation,
  profile: SafetyDataProfile,
): SafetyExclusionRecord['dataSource'] {
  // Gender/age/duplicate checks are deterministic (not from patient disclosures)
  if (rule.ruleId.startsWith('gender-mismatch-') ||
      rule.ruleId.startsWith('age-') ||
      rule.ruleId.startsWith('duplicate-')) {
    return 'deterministic';
  }

  const modalMedsLower = new Set(profile.modalMedications.map(m => m.toLowerCase().trim()));
  const chatMedsLower = new Set(profile.chatMedications.map(m => m.toLowerCase().trim()));
  const modalAllergyLower = new Set(profile.modalAllergies.map(a => a.toLowerCase().trim()));
  const chatAllergyLower = new Set(profile.chatAllergies.map(a => a.toLowerCase().trim()));
  const modalCondLower = new Set(profile.modalConditions.map(c => c.toLowerCase().trim()));
  const chatCondLower = new Set(profile.chatConditions.map(c => c.toLowerCase().trim()));

  let fromModal = false;
  let fromChat = false;

  // Check if the rule was triggered by modal or chat data
  const allModalItems = new Set([...modalMedsLower, ...modalAllergyLower, ...modalCondLower]);
  const allChatItems = new Set([...chatMedsLower, ...chatAllergyLower, ...chatCondLower]);

  // If we can't determine source, default based on which sources have data
  if (allModalItems.size > 0) fromModal = true;
  if (allChatItems.size > 0) fromChat = true;

  if (fromModal && fromChat) return 'both';
  if (fromChat) return 'chat';
  return 'modal';
}

// ── Core Orchestration ──

/**
 * [c1] Run safety validation BEFORE protocol display.
 * [c2] If violations found, strip blocked products and log exclusions.
 * [c3] All exclusions logged with SafetyRule.id and reasoning.
 * [c4] Uses aggregated data from all sources (modals + chat).
 *
 * This function is the single entry point for safety orchestration.
 * Call it after protocol generation, before protocol display.
 */
export async function validateAndSafeGuardProtocol(
  protocol: Protocol,
  safetyProfile: SafetyDataProfile,
  firestoreRules?: SafetyRule[],
  products?: Product[],
): Promise<SafetyOrchestratorResult> {
  // [c4] Aggregate latest safety data from ALL sources
  const aggregated = aggregateSafetyProfile(safetyProfile);

  // Log that we aggregated safety data
  logAudit('safety.profile_aggregated', `protocol:${protocol.id}`, {
    totalMedications: aggregated.medications.length,
    totalAllergies: aggregated.allergies.length,
    totalConditions: aggregated.conditions.length,
    modalMedCount: aggregated.sources.modalMedCount,
    chatMedCount: aggregated.sources.chatMedCount,
    sex: aggregated.sex ?? 'unspecified',
    isPregnant: aggregated.isPregnant ?? false,
  });

  // Fetch safety rules and products if not provided
  const rules = firestoreRules ?? await getActiveSafetyRules();
  const allProducts = products ?? await getAllProducts();

  // Map protocol supplements to Product objects for the safety checker
  const recommendedProducts = protocol.supplements
    .map((s) => allProducts.find((p) => p.id === s.productId))
    .filter((p): p is Product => p !== undefined);

  // [c1] Run deterministic safety checks BEFORE protocol display
  const safetyResult = runSafetyChecks({
    recommendedProducts,
    userMedications: aggregated.medications,
    userConditions: aggregated.conditions,
    userAllergies: aggregated.allergies,
    userSex: aggregated.sex,
    userAge: aggregated.age,
    isPregnant: aggregated.isPregnant,
    firestoreRules: rules,
  });

  const exclusions: SafetyExclusionRecord[] = [];
  const now = new Date().toISOString();

  if (!safetyResult.passed) {
    // [c2] Safety check failed — build exclusion records and strip blocked products
    const blockedProductNames = new Set<string>();

    for (const violation of safetyResult.violations) {
      const exclusion: SafetyExclusionRecord = {
        ruleId: violation.ruleId,
        ruleName: violation.ruleName,
        severity: violation.severity,
        affectedProducts: violation.affectedProducts,
        reasoning: violation.message,
        dataSource: classifyDataSource(violation, safetyProfile),
        timestamp: now,
      };
      exclusions.push(exclusion);

      for (const name of violation.affectedProducts) {
        blockedProductNames.add(name);
      }
    }

    // [c2] Strip blocked products from protocol (protocol regeneration)
    protocol.supplements = protocol.supplements.filter(
      (s) => !blockedProductNames.has(s.name),
    );

    // Add safety warnings to protocol
    protocol.safetyWarnings = [
      ...(protocol.safetyWarnings ?? []),
      ...safetyResult.violations.map((v) => v.message),
    ];

    // [c3] Log all exclusions to audit trail
    logSafetyExclusions(exclusions, protocol.id);

    // Log the regeneration event
    logAudit('safety.protocol_regenerated', `protocol:${protocol.id}`, {
      exclusionCount: exclusions.length,
      removedProducts: [...blockedProductNames].join(', '),
      remainingSupplements: protocol.supplements.length,
    });

    // [c1][c4] Re-validate the stripped protocol to confirm it's now safe
    const remainingProducts = protocol.supplements
      .map((s) => allProducts.find((p) => p.id === s.productId))
      .filter((p): p is Product => p !== undefined);

    if (remainingProducts.length > 0) {
      const recheck = runSafetyChecks({
        recommendedProducts: remainingProducts,
        userMedications: aggregated.medications,
        userConditions: aggregated.conditions,
        userAllergies: aggregated.allergies,
        userSex: aggregated.sex,
        userAge: aggregated.age,
        isPregnant: aggregated.isPregnant,
        firestoreRules: rules,
      });

      if (!recheck.passed) {
        // Second pass found more issues — strip again and log
        const secondBlockedNames = new Set<string>();

        for (const violation of recheck.violations) {
          const exclusion: SafetyExclusionRecord = {
            ruleId: violation.ruleId,
            ruleName: violation.ruleName,
            severity: violation.severity,
            affectedProducts: violation.affectedProducts,
            reasoning: violation.message,
            dataSource: classifyDataSource(violation, safetyProfile),
            timestamp: now,
          };
          exclusions.push(exclusion);

          for (const name of violation.affectedProducts) {
            secondBlockedNames.add(name);
          }
        }

        protocol.supplements = protocol.supplements.filter(
          (s) => !secondBlockedNames.has(s.name),
        );
        protocol.safetyWarnings = [
          ...(protocol.safetyWarnings ?? []),
          ...recheck.violations.map((v) => v.message),
        ];

        logSafetyExclusions(
          exclusions.filter((e) => secondBlockedNames.has(e.affectedProducts[0])),
          protocol.id,
        );
      }

      // Merge second-pass warnings
      safetyResult.warnings.push(...(recheck.warnings ?? []));
      safetyResult.info.push(...(recheck.info ?? []));
    }

    logAudit('safety.validation_failed', `protocol:${protocol.id}`, {
      totalExclusions: exclusions.length,
      finalSupplementCount: protocol.supplements.length,
    });
  } else {
    // Protocol passed — log clean validation
    logAudit('safety.validation_passed', `protocol:${protocol.id}`, {
      supplementCount: protocol.supplements.length,
      warningCount: safetyResult.warnings.length,
    });
  }

  // Add non-blocking warnings to protocol
  if (safetyResult.warnings.length > 0) {
    protocol.safetyWarnings = [
      ...(protocol.safetyWarnings ?? []),
      ...safetyResult.warnings.map((w) => w.message),
    ];
  }

  return {
    protocol,
    passedInitialCheck: safetyResult.passed,
    exclusions,
    warnings: safetyResult.warnings,
    info: safetyResult.info,
    aggregatedProfile: aggregated,
  };
}

/**
 * [c4] Pre-filter products using aggregated safety data from all sources.
 * Use BEFORE sending product list to the LLM.
 */
export async function preFilterWithAggregatedData(
  safetyProfile: SafetyDataProfile,
  firestoreRules?: SafetyRule[],
  products?: Product[],
): Promise<Product[]> {
  const aggregated = aggregateSafetyProfile(safetyProfile);
  const rules = firestoreRules ?? await getActiveSafetyRules();
  const allProducts = products ?? await getAllProducts();

  return preFilterProducts(
    allProducts,
    aggregated.medications,
    aggregated.conditions,
    aggregated.allergies,
    aggregated.sex,
    aggregated.isPregnant,
    rules,
  );
}

/**
 * Build an empty SafetyDataProfile. Convenience for call sites
 * that are incrementally adding data.
 */
export function emptySafetyProfile(): SafetyDataProfile {
  return {
    modalMedications: [],
    modalAllergies: [],
    modalConditions: [],
    chatMedications: [],
    chatAllergies: [],
    chatConditions: [],
  };
}
